import React, { useEffect, useRef } from 'react'
import { connect, useDispatch } from 'react-redux'
import { change, Field, formValueSelector } from 'redux-form'
import EyeMedFormButton from '../EyeMedFormButton'
import { Checkbox, Input, Select } from '../../../../../../../../../../itrust_common/components/form'
import LensFrameDetailsFields from './LensFrameDetailsFields'
import LensDesignMaterialFields from './LensDesignMaterialFields'
import LaboratoryInformation from './LaboratoryInformation'
import { eyemedAvailableLabsLoad } from '../../../../../../../../../../redux/actions/addOns/rpa/vision_insurance/visionInsuranceClaimFormAction'

const PRESCRIPTION_FOCAL_OPTIONS = [
  { label: 'Manual', value: 'Manual' },
  { label: 'Single Vision', value: 'Single Vision' },
  { label: 'MultiFocal', value: 'MultiFocal' },
]

const BASE_OPTIONS = [
  { label: '-', value: '-' },
  { label: 'In', value: 'In' },
  { label: 'Out', value: 'Out' },
  { label: 'Up', value: 'Up' },
  { label: 'Down', value: 'Down' },
]

const filled = (value) => value !== undefined && value !== null && String(value).trim() !== ''

const betweenRange = (min, max, message, { required = false } = {}) => (value) => {
  if (!filled(value)) return required ? message : undefined
  const num = Number(value)
  if (Number.isNaN(num) || num < min || num > max) return message
  return undefined
}

const rightSphereRequired = (value) => (filled(value) ? undefined : 'Right Sphere is Required')
const leftSphereRequired = (value) => (filled(value) ? undefined : 'Left Sphere is Required')
const rightSphereRange = betweenRange(-30, 30, 'Right Sphere must be between -30 and 30')
const leftSphereRange = betweenRange(-30, 30, 'Left Sphere must be between -30 and 30')
const rightAxisRange = betweenRange(0, 180, 'Right Axis must be between 0 and 180')
const leftAxisRange = betweenRange(0, 180, 'Left Axis must be between 0 and 180')
const rightAxisRequired = (value, allValues) => (
  filled(allValues?.lenses?.od?.cylinder) && !filled(value) ? 'Right Axis is Required' : undefined
)
const leftAxisRequired = (value, allValues) => (
  filled(allValues?.lenses?.os?.cylinder) && !filled(value) ? 'Left Axis is Required' : undefined
)
const rightAdd = betweenRange(0, 10, 'Right Add must be between 0 and 10.00')
const leftAdd = betweenRange(0, 10, 'Left Add must be between 0 and 10.00')
const rightPrism1 = betweenRange(0, 10, 'Right Prism 1 must be between 0 and 10')
const leftPrism1 = betweenRange(0, 10, 'Left Prism 1 must be between 0 and 10')
const rightPrism2 = betweenRange(0, 10, 'Right Prism 2 must be between 0 and 10')
const leftPrism2 = betweenRange(0, 10, 'Left Prism 2 must be between 0 and 10')
const distanceRightPd = betweenRange(10, 40, 'Distance Right PD must be between 10 and 40', { required: true })
const distanceLeftPd = betweenRange(10, 40, 'Distance Left PD must be between 10 and 40', { required: true })
const nearRightPd = betweenRange(10, 40, 'Near Right PD must be between 10 and 40')
const nearLeftPd = betweenRange(10, 40, 'Near Left PD must be between 10 and 40')
const rightHeight = betweenRange(6, 40, 'Right Height must be between 6 and 40')
const leftHeight = betweenRange(6, 40, 'Left Height must be between 6 and 40')

const reqLabel = (label) => <>{label} <span className="text-danger">*</span></>

function PrescriptionInput({ name, validate, disabled }) {
  return (
    <Field
      name={name}
      component={Input}
      className="form-control form-control-sm"
      wrapperClasses="mb-0"
      validate={validate}
      disabled={disabled}
    />
  )
}

function BaseSelect({ name, disabled }) {
  return (
    <Field
      name={name}
      component={Select}
      data={BASE_OPTIONS}
      textField="label"
      valueField="value"
      className="custom-select custom-select-sm"
      wrapperClasses="mb-0"
      disabled={disabled}
    />
  )
}

function PdGroup({ title, required, rightName, leftName, rightValidate, leftValidate }) {
  return (
    <div className="col-4 px-2">
      <div className="border rounded overflow-hidden">
        <div className="bg-light px-3 py-2 border-bottom">
          <span className="font-weight-bold small mb-0">{required ? reqLabel(title) : title}</span>
        </div>
        <div className="p-3 d-flex">
          <div className="flex-fill mr-2">
            <PrescriptionInput name={rightName} validate={rightValidate} />
            <div className="text-muted small mt-1 text-center">(RE) mm</div>
          </div>
          <div className="flex-fill ml-2">
            <PrescriptionInput name={leftName} validate={leftValidate} />
            <div className="text-muted small mt-1 text-center">(LE) mm</div>
          </div>
        </div>
      </div>
    </div>
  )
}

function ManualPrescriptionFields({ withPrism, spheresFilled, odCylinder, osCylinder, hasAddValue, isLabType }) {
  const odAxisEnabled = filled(odCylinder)
  const osAxisEnabled = filled(osCylinder)

  return (
    <div className="mt-4 lenses-manual-prescription">
      <div className="table-responsive">
        <table className="table table-bordered table-sm mb-0 lenses-rx-table">
          <thead className="thead-light">
            <tr>
              <th className="lenses-rx-label-col" />
              <th className="text-center">{reqLabel('Sphere')}</th>
              <th className="text-center">Cylinder</th>
              <th className="text-center">Axis</th>
              <th className="text-center">Add</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="font-weight-bold text-nowrap">OD (R)</td>
              <td><PrescriptionInput name="od.sphere" validate={[rightSphereRequired, rightSphereRange]} /></td>
              <td><PrescriptionInput name="od.cylinder" /></td>
              <td>
                <PrescriptionInput
                  name="od.axis"
                  disabled={!odAxisEnabled}
                  validate={odAxisEnabled ? [rightAxisRequired, rightAxisRange] : []}
                />
              </td>
              <td><PrescriptionInput name="od.add" validate={[rightAdd]} /></td>
            </tr>
            <tr>
              <td className="font-weight-bold text-nowrap">OS (L)</td>
              <td><PrescriptionInput name="os.sphere" validate={[leftSphereRequired, leftSphereRange]} /></td>
              <td><PrescriptionInput name="os.cylinder" /></td>
              <td>
                <PrescriptionInput
                  name="os.axis"
                  disabled={!osAxisEnabled}
                  validate={osAxisEnabled ? [leftAxisRequired, leftAxisRange] : []}
                />
              </td>
              <td><PrescriptionInput name="os.add" validate={[leftAdd]} /></td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="table-responsive mt-3">
        <table className="table table-bordered table-sm mb-0 lenses-rx-table">
          <thead className="thead-light">
            <tr>
              <th className="text-nowrap px-2 lenses-rx-label-col">
                <Field
                  name="with_prism"
                  component={Checkbox}
                  label="With Prism"
                  wrapperClasses="mb-0 d-flex align-items-center"
                  format={(value) => Boolean(value)}
                  normalize={(value) => Boolean(value)}
                />
              </th>
              <th className="text-center">Prism 1</th>
              <th className="text-center">Base 1</th>
              <th className="text-center">Prism 2</th>
              <th className="text-center">Base 2</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="font-weight-bold text-nowrap">Prism (R)</td>
              <td><PrescriptionInput name="od.prism_1" disabled={!withPrism} validate={withPrism ? [rightPrism1] : []} /></td>
              <td><BaseSelect name="od.base_1" disabled={!withPrism} /></td>
              <td><PrescriptionInput name="od.prism_2" disabled={!withPrism} validate={withPrism ? [rightPrism2] : []} /></td>
              <td><BaseSelect name="od.base_2" disabled={!withPrism} /></td>
            </tr>
            <tr>
              <td className="font-weight-bold text-nowrap">Prism (L)</td>
              <td><PrescriptionInput name="os.prism_1" disabled={!withPrism} validate={withPrism ? [leftPrism1] : []} /></td>
              <td><BaseSelect name="os.base_1" disabled={!withPrism} /></td>
              <td><PrescriptionInput name="os.prism_2" disabled={!withPrism} validate={withPrism ? [leftPrism2] : []} /></td>
              <td><BaseSelect name="os.base_2" disabled={!withPrism} /></td>
            </tr>
          </tbody>
        </table>
      </div>

      {spheresFilled && (
        <>
          <div className="row mx-n2 mt-4">
            <PdGroup title="Distance PD" required rightName="od.distance_pd" leftName="os.distance_pd" rightValidate={[distanceRightPd]} leftValidate={[distanceLeftPd]} />
            <PdGroup title="Near PD" rightName="od.near_pd" leftName="os.near_pd" rightValidate={[nearRightPd]} leftValidate={[nearLeftPd]} />
            <PdGroup title="Height" rightName="od.height" leftName="os.height" rightValidate={[rightHeight]} leftValidate={[leftHeight]} />
          </div>
          <LensDesignMaterialFields hasAddValue={hasAddValue} />
          <LensFrameDetailsFields isLabType={isLabType} />
        </>
      )}
    </div>
  )
}

function LensesClaim({
  claimId,
  availableLabs,
  selectedLab,
  prescriptionFocal,
  withPrism,
  odSphere,
  osSphere,
  odCylinder,
  osCylinder,
  odAdd,
  osAdd,
  frameDiagnosis,
  specifyBaseCurve,
  thicknessType,
}) {
  const dispatch = useDispatch()
  const labsForUi = availableLabs.filter((lab) => lab?.name && lab?.account)
  const isLabType = labsForUi.length > 0
  const isManual = isLabType || !prescriptionFocal || prescriptionFocal === 'Manual'
  const isPresetFocal = !isLabType && (prescriptionFocal === 'Single Vision' || prescriptionFocal === 'MultiFocal')
  const spheresFilled = isManual && filled(odSphere) && filled(osSphere)
  const hasAddValue = isPresetFocal ? prescriptionFocal === 'MultiFocal' : (filled(odAdd) || filled(osAdd))
  const showLensDesign = spheresFilled || isPresetFocal

  useEffect(() => {
    if (!claimId) return
    dispatch(eyemedAvailableLabsLoad(claimId))
  }, [claimId, dispatch])

  useEffect(() => {
    if (!prescriptionFocal || isLabType) {
      dispatch(change('EyeMedClaimForm', 'lenses.prescription_focal.value', 'Manual'))
    }
  }, [isLabType, prescriptionFocal, dispatch])

  useEffect(() => {
    if (isLabType && !selectedLab) {
      dispatch(change('EyeMedClaimForm', 'lenses.laboratory', labsForUi[0]))
    }
  }, [isLabType, labsForUi, selectedLab, dispatch])

  const prevHasAddValue = useRef(hasAddValue)
  useEffect(() => {
    if (!showLensDesign) return
    if (prevHasAddValue.current !== hasAddValue) {
      dispatch(change('EyeMedClaimForm', 'lenses.lens_type', ''))
      prevHasAddValue.current = hasAddValue
    }
  }, [showLensDesign, hasAddValue, dispatch])

  useEffect(() => {
    if (!showLensDesign) return
    if (!frameDiagnosis) dispatch(change('EyeMedClaimForm', 'lenses.frame_diagnosis', 'Z01.00'))
    if (!specifyBaseCurve) dispatch(change('EyeMedClaimForm', 'lenses.specify_base_curve', 'No'))
    if (isLabType && !thicknessType) {
      dispatch(change('EyeMedClaimForm', 'lenses.thickness_type', 'Lab decides best thickness'))
    }
  }, [showLensDesign, frameDiagnosis, specifyBaseCurve, isLabType, thicknessType, dispatch])

  useEffect(() => {
    if (!filled(odCylinder)) dispatch(change('EyeMedClaimForm', 'lenses.od.axis', ''))
  }, [odCylinder, dispatch])

  useEffect(() => {
    if (!filled(osCylinder)) dispatch(change('EyeMedClaimForm', 'lenses.os.axis', ''))
  }, [osCylinder, dispatch])

  return (
    <div className="row">
      <div className="col-10">
        <div className="bg-white p-6 border rounded">
          {isLabType ? (
            <LaboratoryInformation
              labs={labsForUi}
              selectedLab={selectedLab}
              onSelectLab={(lab) => dispatch(change('EyeMedClaimForm', 'lenses.laboratory', lab))}
            />
          ) : (
            <>
              <h5 className="mb-4 font-weight-bold">Prescription Information</h5>
              <div className="d-flex align-items-center mb-1">
                <label className="mb-0 mr-3 text-nowrap">Prescription Focal:</label>
                <div style={{ width: '18rem' }}>
                  <Field
                    name="prescription_focal.value"
                    component={Select}
                    data={PRESCRIPTION_FOCAL_OPTIONS}
                    textField="label"
                    valueField="value"
                    className="custom-select"
                    wrapperClasses="mb-0"
                  />
                </div>
              </div>
            </>
          )}

          {isManual && (
            <ManualPrescriptionFields
              withPrism={withPrism}
              spheresFilled={spheresFilled}
              odCylinder={odCylinder}
              osCylinder={osCylinder}
              hasAddValue={hasAddValue}
              isLabType={isLabType}
            />
          )}

          {isPresetFocal && (
            <>
              <LensDesignMaterialFields hasAddValue={hasAddValue} />
              <LensFrameDetailsFields isLabType={false} />
            </>
          )}

          <EyeMedFormButton currentModule="Lenses" />
        </div>
      </div>
      <div className="col-2">
        <div className="bg-white border rounded-lg">
          <div className="d-flex flex-column align-items-center">
            <span className="py-5 px-6 w-100 text-primary">Lenses</span>
          </div>
        </div>
      </div>
    </div>
  )
}

const selector = formValueSelector('EyeMedClaimForm')

export default connect((state) => {
  return {
    claimId: state.visionInsuranceClaimForm.visionInsuranceClaimForm?.id,
    availableLabs: state.visionInsuranceClaimForm.eyemedAvailableLabs || [],
    selectedLab: selector(state, 'lenses.laboratory'),
    prescriptionFocal: selector(state, 'lenses.prescription_focal.value'),
    withPrism: selector(state, 'lenses.with_prism'),
    odSphere: selector(state, 'lenses.od.sphere'),
    osSphere: selector(state, 'lenses.os.sphere'),
    odCylinder: selector(state, 'lenses.od.cylinder'),
    osCylinder: selector(state, 'lenses.os.cylinder'),
    odAdd: selector(state, 'lenses.od.add'),
    osAdd: selector(state, 'lenses.os.add'),
    frameDiagnosis: selector(state, 'lenses.frame_diagnosis'),
    specifyBaseCurve: selector(state, 'lenses.specify_base_curve'),
    thicknessType: selector(state, 'lenses.thickness_type'),
  }
})(LensesClaim)
